import React, { useState, useEffect } from 'react';
import { briefs } from '../api';

const HistoryScreen = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await briefs.history();
      setHistory(res.data);
    } catch (err) {
      setError('Failed to load history.');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  };

  const formatTime = (dateStr) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>History</h1>
            <p style={styles.subtitle}>Your archive of past briefs.</p>
          </div>
          <div style={styles.countBadge}>
            <span style={styles.countNum}>{history.length}</span>
            <span style={styles.countLabel}>briefs</span>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Loading */}
        {loading && (
          <div style={styles.skeletonList}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="skeleton" style={styles.skeletonItem} />
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && history.length === 0 && (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>◷</div>
            <div style={styles.emptyTitle}>No briefs yet</div>
            <div style={styles.emptySub}>
              Your brief history will appear here after ANIMA generates your first brief.
            </div>
          </div>
        )}

        {/* List */}
        {!loading && history.length > 0 && (
          <div style={styles.list}>
            {history.map((item, i) => (
              <div
                key={item.id}
                style={{ ...styles.briefItem, animationDelay: `${i * 0.04}s` }}
                className="fade-in"
              >
                {/* Row header */}
                <div style={styles.itemHeader} onClick={() => toggle(item.id)}>
                  <div style={styles.itemLeft}>
                    <div style={styles.itemDateWrap}>
                      <span style={styles.itemDate}>{formatDate(item.created_at)}</span>
                      <span style={styles.itemTime}>{formatTime(item.created_at)}</span>
                    </div>
                  </div>
                  <div style={styles.itemRight}>
                    {item.anima_analysis && (
                      <span style={styles.animaTag}>
                        <span style={styles.animaDot} />
                        ANIMA
                      </span>
                    )}
                    <span style={styles.chevron}>
                      {expanded === item.id ? '▲' : '▼'}
                    </span>
                  </div>
                </div>

                {/* Preview (always visible) */}
                {expanded !== item.id && (
                  <div style={styles.preview}>
                    {(item.anima_analysis || item.content || '').slice(0, 180)}
                    {(item.anima_analysis || item.content || '').length > 180 ? '...' : ''}
                  </div>
                )}

                {/* Expanded content */}
                {expanded === item.id && (
                  <div style={styles.expandedContent}>
                    {item.anima_analysis && (
                      <div style={styles.animaSection}>
                        <div style={styles.animaSectionHeader}>
                          <span style={styles.animaPulse} />
                          <span style={styles.animaSectionLabel}>ANIMA ANALYSIS</span>
                        </div>
                        <div style={styles.animaText}>{item.anima_analysis}</div>
                      </div>
                    )}
                    {item.content && (
                      <div style={styles.contentSection}>
                        <div style={styles.contentLabel}>SUMMARY</div>
                        <div style={styles.contentText}>
                          {item.content.split('\n').map((line, idx) =>
                            line.trim() ? <p key={idx} style={styles.contentPara}>{line}</p> : null
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
  },
  container: {
    maxWidth: 760,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    marginTop: 6,
  },
  countBadge: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 4,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '8px 16px',
  },
  countNum: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--primary)',
  },
  countLabel: {
    fontSize: 12,
    color: 'var(--text-secondary)',
  },
  error: {
    background: 'rgba(255, 77, 109, 0.1)',
    border: '1px solid rgba(255, 77, 109, 0.3)',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 13,
    color: 'var(--danger)',
  },
  skeletonList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  skeletonItem: {
    height: 80,
    borderRadius: 14,
  },
  empty: {
    textAlign: 'center',
    padding: '64px 24px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
  },
  emptyIcon: {
    fontSize: 40,
    color: 'var(--text-dim)',
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: 'var(--text)',
  },
  emptySub: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    maxWidth: 360,
    lineHeight: 1.6,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  briefItem: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    cursor: 'pointer',
    userSelect: 'none',
  },
  itemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  itemDateWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  itemDate: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text)',
  },
  itemTime: {
    fontSize: 11,
    color: 'var(--text-secondary)',
    background: 'var(--surface2)',
    padding: '2px 8px',
    borderRadius: 4,
  },
  itemRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  animaTag: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    background: 'var(--anima-dim)',
    border: '1px solid rgba(123, 97, 255, 0.2)',
    borderRadius: 20,
    padding: '3px 8px',
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.1em',
    color: 'var(--anima)',
  },
  animaDot: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'var(--anima)',
    display: 'inline-block',
  },
  chevron: {
    fontSize: 10,
    color: 'var(--text-secondary)',
  },
  preview: {
    padding: '0 20px 16px',
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
    borderTop: '1px solid var(--border)',
    paddingTop: 12,
  },
  expandedContent: {
    borderTop: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  animaSection: {
    padding: '20px',
    background: 'rgba(123, 97, 255, 0.04)',
    borderBottom: '1px solid var(--border)',
  },
  animaSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  animaPulse: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--anima)',
    boxShadow: '0 0 6px var(--anima)',
    display: 'inline-block',
  },
  animaSectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'var(--anima)',
  },
  animaText: {
    fontSize: 14,
    lineHeight: 1.75,
    color: 'var(--text)',
    borderLeft: '2px solid var(--anima)',
    paddingLeft: 14,
  },
  contentSection: {
    padding: '20px',
  },
  contentLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.14em',
    color: 'var(--text-secondary)',
    marginBottom: 12,
  },
  contentText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  contentPara: {
    fontSize: 14,
    lineHeight: 1.75,
    color: 'var(--text)',
  },
};

export default HistoryScreen;
